import { useCallback, useMemo, useState } from 'react'
import { Pencil, Plus, Trash2, Hash, Gavel, Ban, Undo2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge } from '@/components/StatusBadge'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useToast } from '@/providers/UiProviders'
import {
  fetchNumbers,
  createNumber,
  updateNumber,
  deleteNumber,
  setNumberStatus,
  sendToAuction,
  type NumberRow,
} from './numbersApi'

type EditTarget = NumberRow | 'new' | null

const statusVariant = (s: string): 'active' | 'pending' | 'deleted' =>
  s === 'available' ? 'active' : s === 'auction' ? 'pending' : 'deleted'
const statusLabel = (s: string): string =>
  ({ available: 'Сотувда', auction: 'Аукцион', reserved: 'Банд', sold: 'Сотилган', blocked: 'Блок' })[s] ?? s

const money = (v: number) => v.toLocaleString('ru-RU').replace(/,/g, ' ')

/**
 * VIP.01 — Чиройли (VIP) рақамлар + аукцион. Фақат +999/+777/+555. Қўшиш/тахрир,
 * нарх белгилаш, банд/блок қилиш, аукционга чиқариш (бошланғич нарх + муддат).
 */
export function VipNumbers() {
  const { toast } = useToast()
  const [target, setTarget] = useState<EditTarget>(null)
  const [auctionTarget, setAuctionTarget] = useState<NumberRow | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast('Готово', { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast('Ошибка', { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
      }
    },
    [toast],
  )

  const columns = useMemo<AdminColumn<NumberRow>[]>(
    () => [
      {
        key: 'display',
        header: 'Рақам',
        type: 'text',
        minWidth: 200,
        render: (r) => (
          <span className="flex items-center gap-2">
            <Hash size={14} className="text-tertiary" />
            <span className="font-semibold text-primary">{r.display}</span>
            <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-xs font-semibold text-amber-600">+{r.prefix}</span>
          </span>
        ),
      },
      {
        key: 'price',
        header: 'Нарх / Ставка',
        type: 'text',
        width: 160,
        align: 'right',
        render: (r) =>
          r.status === 'auction' ? (
            <span className="text-primary">
              {money(r.auction_current_bid || r.auction_start_price)} <span className="text-xs text-tertiary">({r.bids})</span>
            </span>
          ) : (
            <span className="text-primary">{money(r.price)}</span>
          ),
      },
      {
        key: 'status',
        header: 'Статус',
        type: 'text',
        width: 120,
        render: (r) => <StatusBadge status={statusVariant(r.status)} label={statusLabel(r.status)} />,
      },
    ],
    [],
  )

  // A sold number belongs to a user — no edit / delete / re-auction for it.
  const notSold = (r: NumberRow) => r.status !== 'sold'
  const rowActions = useMemo<RowAction<NumberRow>[]>(
    () => [
      { id: 'edit', label: 'Изменить', icon: <Pencil size={13} />, visible: notSold, onSelect: (r) => setTarget(r) },
      {
        id: 'auction',
        label: 'На аукцион',
        icon: <Gavel size={13} />,
        visible: (r) => r.status !== 'sold' && r.status !== 'auction',
        onSelect: (r) => setAuctionTarget(r),
      },
      {
        id: 'block',
        label: 'Заблокировать',
        icon: <Ban size={13} />,
        visible: (r) => r.status !== 'blocked' && r.status !== 'sold',
        onSelect: (r) => void run(() => setNumberStatus(r.id, 'blocked')),
      },
      {
        id: 'unblock',
        label: 'Вернуть в продажу',
        icon: <Undo2 size={13} />,
        visible: (r) => r.status !== 'available' && r.status !== 'sold',
        onSelect: (r) => void run(() => setNumberStatus(r.id, 'available')),
      },
      { id: 'delete', label: 'Удалить', icon: <Trash2 size={13} />, danger: true, visible: notSold, onSelect: (r) => void run(() => deleteNumber(r.id)) },
    ],
    [run],
  )

  return (
    <>
      <PageHeader
        title="VIP рақамлар"
        screenId="VIP.01"
        actions={
          <Button size="sm" onClick={() => setTarget('new')}>
            <Plus size={14} /> Добавить
          </Button>
        }
      />
      <DataTable<NumberRow>
        key={reloadKey}
        screenId="VIP.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchNumbers}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <Hash size={22} />, title: 'Пока нет номеров' }}
      />
      {target && (
        <NumberDialog
          target={target}
          onClose={() => setTarget(null)}
          onSaved={() => {
            setTarget(null)
            setReloadKey((k) => k + 1)
          }}
        />
      )}
      {auctionTarget && (
        <AuctionDialog
          row={auctionTarget}
          onClose={() => setAuctionTarget(null)}
          onSaved={() => {
            setAuctionTarget(null)
            setReloadKey((k) => k + 1)
          }}
        />
      )}
    </>
  )
}

function NumberDialog({ target, onClose, onSaved }: { target: EditTarget; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast()
  const editing = target !== 'new' && target !== null ? target : null
  const [display, setDisplay] = useState(editing?.display ?? '+999 ')
  const [pattern, setPattern] = useState(editing?.pattern ?? '')
  const [price, setPrice] = useState(String(editing?.price ?? ''))
  const [busy, setBusy] = useState(false)

  const save = async () => {
    const digits = display.replace(/\D/g, '')
    const prefix = ['999', '777', '555'].find((p) => digits.startsWith(p))
    if (!prefix) {
      toast('Рақам +999 / +777 / +555 билан бошланиши керак', { tone: 'error' })
      return
    }
    if (digits.length < 9) {
      toast('Тўлиқ рақам киритинг', { tone: 'error', description: 'Масалан: +999 99 999-99-99' })
      return
    }
    setBusy(true)
    try {
      const body = { display: display.trim(), pattern: pattern.trim(), price: Number(price) || 0 }
      if (editing) await updateNumber(editing.id, body)
      else await createNumber(body)
      toast('Готово', { tone: 'success' })
      onSaved()
    } catch (e) {
      setBusy(false)
      toast('Сақлаб бўлмади', { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={editing ? 'Изменить номер' : 'Новый VIP номер'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={save} disabled={busy || !display.trim()}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Полный номер (начинается с +999 / +777 / +555)</span>
          <Input value={display} onChange={(e) => setDisplay(e.target.value)} mono autoFocus placeholder="+999 99 999-99-99" />
          <span className="text-xs text-tertiary">Введите номер целиком, напр. +999 99 999-99-99 — не только префикс</span>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Метка (gold, AABB…) — необязательно</span>
          <Input value={pattern} onChange={(e) => setPattern(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Цена (сум)</span>
          <Input value={price} onChange={(e) => setPrice(e.target.value.replace(/\D/g, ''))} mono placeholder="5000000" />
        </label>
      </div>
    </Dialog>
  )
}

function AuctionDialog({ row, onClose, onSaved }: { row: NumberRow; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast()
  const [start, setStart] = useState(String(row.auction_start_price || row.price || ''))
  const [days, setDays] = useState('7')
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      const d = Number(days) || 0
      const ends = d > 0 ? new Date(Date.now() + d * 86400000).toISOString() : null
      await sendToAuction(row.id, Number(start) || 0, ends)
      toast('На аукционе', { tone: 'success' })
      onSaved()
    } catch (e) {
      setBusy(false)
      toast('Ошибка', { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Аукцион — ${row.display}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={save} disabled={busy}>
            На аукцион
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Начальная цена (сум)</span>
          <Input value={start} onChange={(e) => setStart(e.target.value.replace(/\D/g, ''))} mono placeholder="1000000" />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Длительность (дней, 0 = без срока)</span>
          <Input value={days} onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))} mono placeholder="7" />
        </label>
      </div>
    </Dialog>
  )
}
